/* ==========================================================================
   Builders Supply Pro - Global JavaScript
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Utility: Debounce
     ------------------------------------------------------------------------ */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  /* ------------------------------------------------------------------------
     Utility: Fetch with JSON
     ------------------------------------------------------------------------ */
  function fetchJSON(url, options) {
    var defaults = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
    var config = Object.assign({}, defaults, options);
    return fetch(url, config).then(function (response) {
      if (!response.ok) {
        throw new Error('Network response was not ok: ' + response.status);
      }
      return response.json();
    });
  }

  /* ------------------------------------------------------------------------
     Utility: Format money (cents to dollars)
     ------------------------------------------------------------------------ */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = (parseInt(cents, 10) / 100).toFixed(2);
    return '$' + value;
  }

  /* ------------------------------------------------------------------------
     Section Rendering API Helper
     Fetches a section's HTML without a full page reload.
     Usage: renderSection('collection-grid', '/collections/lumber?sort_by=price-ascending')
     ------------------------------------------------------------------------ */
  function renderSection(sectionId, url) {
    var separator = url.indexOf('?') !== -1 ? '&' : '?';
    var sectionUrl = url + separator + 'section_id=' + sectionId;
    return fetch(sectionUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        return temp;
      });
  }

  /* ------------------------------------------------------------------------
     Cart Module
     ------------------------------------------------------------------------ */
  var Cart = {
    /**
     * Add item to cart
     * @param {number} variantId - The variant ID
     * @param {number} quantity - Quantity to add
     * @param {object} [properties] - Optional line item properties
     * @returns {Promise}
     */
    add: function (variantId, quantity, properties) {
      var body = {
        id: variantId,
        quantity: quantity || 1
      };
      if (properties) {
        body.properties = properties;
      }
      return fetchJSON(window.routes.cart_add_url + '.js', {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(function (item) {
        Cart.updateCount();
        Cart.refreshDrawer();
        Cart.showToast(item.title + ' added to cart');
        document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));
        return item;
      }).catch(function (error) {
        console.error('Cart add error:', error);
        Cart.showToast(window.cartStrings.error || 'Error adding to cart', 'error');
        throw error;
      });
    },

    /**
     * Update item quantity by line index (1-based)
     * @param {number} line - Line index (1-based)
     * @param {number} quantity - New quantity (0 to remove)
     * @returns {Promise}
     */
    updateLine: function (line, quantity) {
      return fetchJSON(window.routes.cart_change_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function (cart) {
        Cart.updateCount(cart.item_count);
        Cart.refreshDrawer();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return cart;
      }).catch(function (error) {
        console.error('Cart update error:', error);
        Cart.showToast(window.cartStrings.error || 'Error updating cart', 'error');
        throw error;
      });
    },

    /**
     * Update item quantity by variant key
     * @param {string} key - The line item key (variantId:hash)
     * @param {number} quantity - New quantity (0 to remove)
     * @returns {Promise}
     */
    updateByKey: function (key, quantity) {
      return fetchJSON(window.routes.cart_change_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (cart) {
        Cart.updateCount(cart.item_count);
        Cart.refreshDrawer();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return cart;
      }).catch(function (error) {
        console.error('Cart update error:', error);
        throw error;
      });
    },

    /**
     * Remove item from cart
     * @param {number} line - Line index (1-based)
     * @returns {Promise}
     */
    remove: function (line) {
      return Cart.updateLine(line, 0);
    },

    /**
     * Get current cart state
     * @returns {Promise}
     */
    get: function () {
      return fetchJSON(window.routes.cart_url + '.js');
    },

    /**
     * Update the cart count badge in the header
     * @param {number} [count] - If provided, use this count. Otherwise fetch from API.
     */
    updateCount: function (count) {
      if (typeof count !== 'undefined') {
        setCartCount(count);
      } else {
        Cart.get().then(function (cart) {
          setCartCount(cart.item_count);
        });
      }

      function setCartCount(n) {
        var badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(function (badge) {
          badge.textContent = n;
          badge.setAttribute('data-count', n);
          // show the badge when there are items, hide it at zero (markup starts hidden at 0)
          badge.style.display = Number(n) > 0 ? '' : 'none';
        });
      }
    },

    /**
     * Refresh the cart drawer contents
     */
    refreshDrawer: function () {
      var body = document.getElementById('CartDrawerBody');
      var footer = document.getElementById('CartDrawerFooter');
      if (!body || !footer) return;

      Cart.get().then(function (cart) {
        if (cart.item_count === 0) {
          body.innerHTML = '<div class="cart-drawer__empty">Your cart is empty</div>';
          footer.innerHTML = '';
          return;
        }

        var itemsHtml = '';
        cart.items.forEach(function (item, index) {
          var imageSrc = item.image
            ? item.image.replace(/(\.[^.]+)$/, '_120x120$1')
            : '';
          var variantTitle = item.variant_title || '';
          itemsHtml += '<div class="cart-drawer__item" data-line="' + (index + 1) + '" data-key="' + item.key + '">' +
            (imageSrc ? '<img class="cart-drawer__item-image" src="' + imageSrc + '" alt="" loading="lazy">' : '<div class="cart-drawer__item-image"></div>') +
            '<div class="cart-drawer__item-info">' +
              '<div class="cart-drawer__item-title"><a href="' + item.url + '">' + item.title + '</a></div>' +
              (variantTitle ? '<div class="cart-drawer__item-variant">' + variantTitle + '</div>' : '') +
              '<div class="cart-drawer__item-bottom">' +
                '<div class="quantity-selector" data-line="' + (index + 1) + '">' +
                  '<button class="quantity-selector__btn" data-action="decrease" aria-label="Decrease quantity">&minus;</button>' +
                  '<input class="quantity-selector__input" type="number" value="' + item.quantity + '" min="0" max="9999" aria-label="Quantity">' +
                  '<button class="quantity-selector__btn" data-action="increase" aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<span class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
              '</div>' +
              '<button class="cart-drawer__item-remove" data-line="' + (index + 1) + '">Remove</button>' +
            '</div>' +
          '</div>';
        });

        body.innerHTML = itemsHtml;

        footer.innerHTML =
          '<div class="cart-drawer__subtotal">' +
            '<span>Subtotal</span>' +
            '<span class="cart-drawer__subtotal-amount">' + formatMoney(cart.total_price) + '</span>' +
          '</div>' +
          '<div class="cart-drawer__shipping-note">Shipping &amp; taxes calculated at checkout</div>' +
          '<a href="/checkout" class="btn btn-primary cart-drawer__checkout-btn">Proceed to Checkout</a>' +
          '<a href="/cart" class="cart-drawer__continue">View full cart</a>';
      });
    },

    /**
     * Show a toast notification
     * @param {string} message
     * @param {string} [type='success'] - 'success' or 'error'
     */
    showToast: function (message, type) {
      var existing = document.querySelector('.toast');
      if (existing) existing.remove();

      var toast = document.createElement('div');
      toast.className = 'toast' + (type === 'error' ? ' toast--error' : ' toast--success');
      toast.textContent = message;
      document.body.appendChild(toast);

      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });

      setTimeout(function () {
        toast.classList.remove('is-visible');
        setTimeout(function () {
          toast.remove();
        }, 300);
      }, 3000);
    }
  };

  /* Expose Cart globally */
  window.Cart = Cart;

  /* ------------------------------------------------------------------------
     Cart Drawer Toggle
     ------------------------------------------------------------------------ */
  function initCartDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    /* Open triggers */
    document.querySelectorAll('[data-cart-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openCartDrawer();
      });
    });

    /* Close triggers */
    drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeCartDrawer();
      });
    });

    /* Close on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeCartDrawer();
      }
    });

    function openCartDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('scroll-locked');
      Cart.refreshDrawer();
    }

    function closeCartDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('scroll-locked');
    }

    /* Delegate events inside drawer */
    drawer.addEventListener('click', function (e) {
      /* Remove item */
      var removeBtn = e.target.closest('.cart-drawer__item-remove');
      if (removeBtn) {
        var line = parseInt(removeBtn.getAttribute('data-line'), 10);
        Cart.remove(line);
        return;
      }

      /* Quantity buttons */
      var qtyBtn = e.target.closest('.quantity-selector__btn');
      if (qtyBtn) {
        var selector = qtyBtn.closest('.quantity-selector');
        var input = selector.querySelector('.quantity-selector__input');
        var currentQty = parseInt(input.value, 10) || 1;
        var action = qtyBtn.getAttribute('data-action');
        var newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        var drawerLine = parseInt(selector.getAttribute('data-line'), 10);
        Cart.updateLine(drawerLine, newQty);
      }
    });

    /* Listen for cart:added to open drawer */
    document.addEventListener('cart:added', function () {
      openCartDrawer();
    });
  }

  /* ------------------------------------------------------------------------
     Quantity Selector (on product pages and collection pages)
     ------------------------------------------------------------------------ */
  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.quantity-selector__btn');
      if (!btn) return;

      /* Skip if inside cart drawer (handled separately) */
      if (btn.closest('#CartDrawer')) return;

      var selector = btn.closest('.quantity-selector');
      var input = selector.querySelector('.quantity-selector__input');
      var current = parseInt(input.value, 10) || 1;
      var action = btn.getAttribute('data-action');
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 9999;

      if (action === 'increase') {
        input.value = Math.min(current + 1, max);
      } else if (action === 'decrease') {
        input.value = Math.max(current - 1, min);
      }

      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------------
     Quick Add to Cart (from collection pages)
     ------------------------------------------------------------------------ */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();
      var variantId = btn.getAttribute('data-quick-add');
      if (!variantId) return;

      /* Check for sibling quantity selector */
      var card = btn.closest('.product-card, .product-list-item, tr');
      var qtyInput = card ? card.querySelector('.quantity-selector__input') : null;
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      /* Disable button while processing */
      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = 'Adding...';

      Cart.add(parseInt(variantId, 10), quantity).finally(function () {
        btn.disabled = false;
        btn.textContent = originalText;
      });
    });
  }

  /* ------------------------------------------------------------------------
     Mobile Menu Toggle
     ------------------------------------------------------------------------ */
  function initMobileMenu() {
    var toggle = document.querySelector('[data-mobile-menu-toggle]');
    var nav = document.querySelector('.mobile-nav');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var isOpen = nav.classList.toggle('is-open');
      document.body.classList.toggle('scroll-locked', isOpen);
    });

    nav.querySelector('.mobile-nav__overlay').addEventListener('click', function () {
      nav.classList.remove('is-open');
      document.body.classList.remove('scroll-locked');
    });

    var closeBtn = nav.querySelector('[data-mobile-menu-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        nav.classList.remove('is-open');
        document.body.classList.remove('scroll-locked');
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        nav.classList.remove('is-open');
        document.body.classList.remove('scroll-locked');
      }
    });
  }

  /* ------------------------------------------------------------------------
     Announcement Bar Dismiss
     ------------------------------------------------------------------------ */
  function initAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar');
    if (!bar) return;

    var dismissBtn = bar.querySelector('.announcement-bar__dismiss');
    if (!dismissBtn) return;

    /* Check if previously dismissed this session */
    if (sessionStorage.getItem('announcement-dismissed') === 'true') {
      bar.hidden = true;
      return;
    }

    dismissBtn.addEventListener('click', function () {
      bar.hidden = true;
      sessionStorage.setItem('announcement-dismissed', 'true');
    });
  }

  /* ------------------------------------------------------------------------
     Accordion Toggle (Filter Groups)
     ------------------------------------------------------------------------ */
  function initAccordions() {
    document.addEventListener('click', function (e) {
      var header = e.target.closest('.filter-group__header');
      if (!header) return;

      var group = header.closest('.filter-group');
      if (group) {
        group.classList.toggle('is-collapsed');
      }
    });
  }

  /* ------------------------------------------------------------------------
     Image Lazy Loading (Intersection Observer)
     ------------------------------------------------------------------------ */
  function initLazyImages() {
    if (!('IntersectionObserver' in window)) {
      /* Fallback: load all images immediately */
      document.querySelectorAll('[data-src]').forEach(function (img) {
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.getAttribute('data-src')) {
            img.src = img.getAttribute('data-src');
            img.removeAttribute('data-src');
          }
          if (img.getAttribute('data-srcset')) {
            img.srcset = img.getAttribute('data-srcset');
            img.removeAttribute('data-srcset');
          }
          img.classList.add('is-loaded');
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '200px 0px',
      threshold: 0.01
    });

    document.querySelectorAll('[data-src]').forEach(function (img) {
      observer.observe(img);
    });
  }

  /* ------------------------------------------------------------------------
     Predictive Search
     ------------------------------------------------------------------------ */
  function initPredictiveSearch() {
    var searchInput = document.querySelector('.header__search-input');
    var searchDropdown = document.querySelector('.predictive-search');
    if (!searchInput || !searchDropdown) return;

    var predictiveUrl = window.routes.predictive_search_url;
    if (!predictiveUrl) return;

    var doSearch = debounce(function (query) {
      if (query.length < 2) {
        searchDropdown.classList.remove('is-active');
        searchDropdown.innerHTML = '';
        return;
      }

      fetch(predictiveUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search')
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var temp = document.createElement('div');
          temp.innerHTML = html;
          var results = temp.querySelector('.predictive-search-results');
          if (results) {
            searchDropdown.innerHTML = results.innerHTML;
            searchDropdown.classList.add('is-active');
          } else {
            searchDropdown.classList.remove('is-active');
          }
        })
        .catch(function () {
          searchDropdown.classList.remove('is-active');
        });
    }, 250);

    searchInput.addEventListener('input', function () {
      doSearch(this.value.trim());
    });

    searchInput.addEventListener('focus', function () {
      if (this.value.trim().length >= 2) {
        doSearch(this.value.trim());
      }
    });

    /* Close dropdown on click outside */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.header__search')) {
        searchDropdown.classList.remove('is-active');
      }
    });

    /* Close on Escape */
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        searchDropdown.classList.remove('is-active');
        searchInput.blur();
      }
    });
  }

  /* ------------------------------------------------------------------------
     View Mode Toggle
     ------------------------------------------------------------------------ */
  function initViewToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.view-toggle__btn');
      if (!btn) return;

      var mode = btn.getAttribute('data-view');
      if (!mode) return;

      /* Update toggle buttons */
      btn.closest('.view-toggle').querySelectorAll('.view-toggle__btn').forEach(function (b) {
        b.classList.remove('is-active');
      });
      btn.classList.add('is-active');

      /* Update product grid */
      var grid = document.querySelector('[data-product-grid]');
      if (grid) {
        grid.setAttribute('data-view-mode', mode);
        grid.className = grid.className
          .replace(/view-mode--\w+/g, '')
          .trim() + ' view-mode--' + mode;
      }

      /* Persist preference */
      try {
        localStorage.setItem('product-view-mode', mode);
      } catch (e) { /* ignore */ }
    });

    /* Restore saved preference */
    try {
      var saved = localStorage.getItem('product-view-mode');
      if (saved) {
        var btn = document.querySelector('.view-toggle__btn[data-view="' + saved + '"]');
        if (btn) btn.click();
      }
    } catch (e) { /* ignore */ }
  }

  /* ------------------------------------------------------------------------
     Product Detail Page: Thumbnail Gallery
     ------------------------------------------------------------------------ */
  function initProductGallery() {
    var mainImage = document.querySelector('.product-detail__main-image');
    var thumbnails = document.querySelectorAll('.product-detail__thumbnail');
    if (!mainImage || !thumbnails.length) return;

    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbnails.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        mainImage.src = thumb.getAttribute('data-full-src') || thumb.src.replace('_120x120', '');
        mainImage.alt = thumb.alt || '';
      });
    });
  }

  /* ------------------------------------------------------------------------
     Quick View Modal
     ------------------------------------------------------------------------ */
  function initQuickView() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;

      e.preventDefault();
      var productUrl = trigger.getAttribute('data-quick-view');
      if (!productUrl) return;

      /* Fetch product page section */
      renderSection('quick-view', productUrl).then(function (container) {
        var content = container.querySelector('.quick-view__content');
        if (!content) return;

        var overlay = document.querySelector('.quick-view-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'quick-view-overlay';
          overlay.innerHTML = '<div class="quick-view"><button class="quick-view__close" aria-label="Close">&times;</button><div class="quick-view__body"></div></div>';
          document.body.appendChild(overlay);
        }

        overlay.querySelector('.quick-view__body').innerHTML = content.innerHTML;

        requestAnimationFrame(function () {
          overlay.classList.add('is-active');
          document.body.classList.add('scroll-locked');
        });

        /* Close handlers */
        function closeQuickView() {
          overlay.classList.remove('is-active');
          document.body.classList.remove('scroll-locked');
        }

        overlay.querySelector('.quick-view__close').addEventListener('click', closeQuickView);
        overlay.addEventListener('click', function (ev) {
          if (ev.target === overlay) closeQuickView();
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     AJAX Collection Filtering/Sorting
     ------------------------------------------------------------------------ */
  function initCollectionFilters() {
    var filterForm = document.querySelector('[data-filter-form]');
    if (!filterForm) return;

    filterForm.addEventListener('change', debounce(function () {
      var formData = new FormData(filterForm);
      var params = new URLSearchParams(formData).toString();
      var baseUrl = window.location.pathname;
      var newUrl = baseUrl + (params ? '?' + params : '');

      /* Show loading state */
      var grid = document.querySelector('[data-product-grid]');
      if (grid) {
        grid.style.opacity = '0.5';
        grid.style.pointerEvents = 'none';
      }

      var sectionId = filterForm.getAttribute('data-section-id') || 'collection-grid';

      renderSection(sectionId, newUrl).then(function (container) {
        var newGrid = container.querySelector('[data-product-grid]');
        if (newGrid && grid) {
          grid.innerHTML = newGrid.innerHTML;
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
        }

        /* Update active filter tags */
        var newFilters = container.querySelector('.active-filters');
        var currentFilters = document.querySelector('.active-filters');
        if (newFilters && currentFilters) {
          currentFilters.innerHTML = newFilters.innerHTML;
        }

        /* Update count */
        var newCount = container.querySelector('.collection-toolbar__count');
        var currentCount = document.querySelector('.collection-toolbar__count');
        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }

        /* Update URL without reload */
        history.replaceState(null, '', newUrl);
      }).catch(function () {
        if (grid) {
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
        }
      });
    }, 300));
  }

  /* ------------------------------------------------------------------------
     Initialize All Modules on DOMContentLoaded
     ------------------------------------------------------------------------ */
  function init() {
    initCartDrawer();
    initQuantitySelectors();
    initQuickAdd();
    initMobileMenu();
    initAnnouncementBar();
    initAccordions();
    initLazyImages();
    initPredictiveSearch();
    initViewToggle();
    initProductGallery();
    initQuickView();
    initCollectionFilters();

    /* Update cart count on page load */
    Cart.updateCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
